<template>
  <!-- 导航项容器：支持展开/收缩状态，悬停效果和选中状态 -->
  <div class="nav-item-wrapper">
    <!-- 一级导航项 -->
    <div
      class="nav-animate overflow-hidden items-center flex cursor-pointer"
      :class="[
        short ? 'w-[48px] h-[48px] justify-center' : 'w-[204px] h-[48px] justify-between px-[8px]',
        select ? 'rounded-xl' : 'hover:rounded-xl',
      ]"
      :title="name"
      @mouseenter="onMouseenter"
      @click="onClickNavItem"
    >
      <!-- 左侧内容：图标 + 文本信息 -->
      <div class="flex items-center flex-1">
        <!-- 图标容器：固定尺寸，选中时显示背景高亮 -->
        <div
          class="w-[48px] h-[48px] rounded-xl flex items-center justify-center flex-shrink-0"
          :class="[select ? 'rounded-xl bg-[rgba(170, 194, 233, 0.22)] icon-select' : '']"
        >
          <svg-icon
            :name="icon"
            size="24"
            class="fill-white-primary text-white"
            :style="{ color: '#ffffff' }"
          />
        </div>

        <!-- 文本信息区域：仅在非收缩状态下显示，带淡入淡出动画 -->
        <transition name="fade">
          <div
            v-if="!short"
            class="flex flex-col flex-1 h-[48px] pl-[16px] justify-center min-w-0"
          >
            <!-- 导航项主标题 -->
            <Text
              :content="name"
              color="var(--aix-text-color-white-primary)"
              size="title"
            />
            <!-- 导航项描述文本 -->
            <Text
              :content="desc"
              color="var(--aix-text-color-white-placeholder)"
              size="small"
            />
          </div>
        </transition>
      </div>

      <!-- 右侧箭头：仅在有子菜单且非收缩状态下显示 -->
      <transition name="fade">
        <div
          v-if="hasChildren && !short"
          class="flex items-center justify-center w-[24px] h-[24px] flex-shrink-0 ml-[8px] arrow-container"
          @click.stop="toggleExpanded"
        >
          <ChevronDown
            :size="16"
            class="transition-transform duration-200"
            :class="{ 'rotate-180': isExpanded }"
            :stroke-width="1"
          />
        </div>
      </transition>
    </div>

    <!-- 二级菜单：仅在展开状态且非收缩模式下显示 -->
    <transition name="slide-down">
      <div
        v-if="hasChildren && isExpanded && !short"
        class="secondary-menu mt-[8px] ml-[16px] space-y-[4px]"
      >
        <div
          v-for="(childRoute, index) in visibleChildren"
          :key="childRoute.path || index"
          class="secondary-menu-item flex items-center h-[36px] px-[16px] rounded-lg cursor-pointer transition-colors duration-200"
          :class="[
            isChildSelected(childRoute)
              ? 'second-menu-item-selected text-white'
              : 'text-[rgba(255,255,255,0.7)] hover:bg-[rgba(255,255,255,0.1)] hover:text-white',
          ]"
          @click="onClickChildItem(childRoute)"
        >
          <!-- 子菜单标题 -->
          <Text
            :content="childRoute.meta?.title || childRoute.meta?.name || childRoute.name"
            :color="isChildSelected(childRoute) ? '#ffffff' : 'rgba(255,255,255,0.7)'"
            size="normal"
          />
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
// 导入路由状态管理和相关类型定义
import { useRouterStore } from '@/store/global/router.store';
import { storeToRefs } from 'pinia';
import type { PropType } from 'vue';
import { type RouteRecordNormalized, useRouter, useRoute } from 'vue-router';
import { SvgIcon, Text } from '@tencent/aix-design';
import { ChevronDown } from 'lucide-vue-next';
import { computed, ref, watch, onMounted } from 'vue';

// 获取路由状态管理实例和响应式引用
const routerStore = useRouterStore();
const { isShowSecondMenu, collapsed } = storeToRefs(routerStore);
const router = useRouter();
const currentRoute = useRoute();

// 组件属性定义
const props = defineProps({
  short: {
    // 是否为收缩状态（仅显示图标）
    type: Boolean,
    default: false,
  },
  icon: {
    // 导航项图标名称
    type: String,
    default: '',
  },
  name: {
    // 导航项主标题
    type: String,
    default: '',
  },
  desc: {
    // 导航项描述文本
    type: String,
    default: '',
  },
  select: {
    // 是否为当前选中状态
    type: Boolean,
    default: false,
  },
  route: {
    // 关联的路由对象，用于导航跳转
    type: Object as PropType<RouteRecordNormalized>,
    required: true,
  },
});

// 事件发射器定义
const emits = defineEmits(['onShow', 'onNavClick']);

// 展开状态管理
const isExpanded = ref(false);

// 计算属性：是否有子菜单
const hasChildren = computed(() => {
  return props.route.children && props.route.children.length > 0;
});

// 计算属性：可见的子路由
const visibleChildren = computed(() => {
  if (!props.route.children) return [];

  return props.route.children.filter(
    (child) => !child.meta?.hide && !child.meta?.isHidePc && (child.meta?.title || child.meta?.name || child.name),
  );
});

// 监听选中状态变化，当选中时自动展开有子菜单的项
watch(
  () => props.select,
  (newSelect) => {
    if (newSelect && hasChildren.value) {
      isExpanded.value = true;
    } else if (!newSelect) {
      isExpanded.value = false;
    }
  },
  { immediate: true },
);

// 判断子菜单项是否被选中
const isChildSelected = (childRoute: RouteRecordNormalized) => {
  console.log('子菜单选中：', childRoute);
  return (
    currentRoute.path === childRoute.path ||
    currentRoute.name === childRoute.name ||
    currentRoute.matched.some((matched) => matched.name === childRoute.name)
  );
};

/**
 * 切换展开/收缩状态
 */
const toggleExpanded = () => {
  isExpanded.value = !isExpanded.value;
};

/**
 * 鼠标悬停事件处理
 * 功能：根据折叠状态决定是否显示二级菜单
 */
const onMouseenter = () => {
  if (props.select) {
    // 仅当当前导航项被选中时才显示二级菜单
    isShowSecondMenu.value = true;
  }

  // 根据折叠状态决定是否显示 secondlevelbar
  // 折叠状态下：hover 时显示 secondlevelbar
  // 展开状态下：hover 时不显示 secondlevelbar
  if (collapsed.value) {
    emits('onShow', true);
  } else {
    emits('onShow', false);
  }

  // 设置当前路由为一级路由，用于二级菜单的显示
  routerStore.setFirstRoute(props.route);
};

// 监听collapsed 状态通知父组件显示相关内容
watch(
  collapsed,
  (newValue) => {
    console.log('子组件，collapsed:', newValue);
    // 折叠状态和展开状态不显示secondlevelbar
    // 折叠状态，鼠标hover悬停图标和secondlevelbar状态显示secondlevelbar
    // 展开状态鼠标hover悬停不显示secondlevelbar

    // 无论折叠还是展开状态，默认都不显示 secondlevelbar
    // secondlevelbar 的显示由 hover 事件根据折叠状态控制
    emits('onShow', false);
  },
  { immediate: true },
);

/**
 * 点击一级菜单事件处理
 * 功能：选中当前项并展开子菜单（如果有），或进行路由跳转，同时通知父组件展开侧边栏
 */
const onClickNavItem = () => {
  // 通知父组件展开侧边栏
  emits('onNavClick');

  // 设置当前路由为选中状态
  isShowSecondMenu.value = true;
  routerStore.setFirstRoute(props.route);

  if (hasChildren.value && !props.short) {
    // 有子菜单且非收缩状态：展开子菜单
    isExpanded.value = true;

    // 如果有默认子路由，跳转到第一个可见的子路由
    const firstVisibleChild = visibleChildren.value[0];
    if (firstVisibleChild && firstVisibleChild.path) {
      router.push(firstVisibleChild.path);
    }
  } else {
    // 无子菜单或收缩状态：直接跳转
    if (props.route.path && props.route.path !== '/') {
      router.push(props.route.path);
    }
  }
};

/**
 * 点击子菜单项事件处理
 * 功能：跳转到子路由
 */
const onClickChildItem = (childRoute: RouteRecordNormalized) => {
  if (childRoute.path) {
    router.push(childRoute.path);
  }
};
</script>

<style scoped lang="scss">
/* 导航项尺寸变化的平滑过渡动画 */
.nav-animate {
  transition: width, height;
  transition: width 0.25s cubic-bezier(0.4, 0, 0.2, 1), height 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  will-change: width, height;
  transform: translateZ(0); /* 启用硬件加速 */
}

/* 确保所有图标都显示为白色 */
:deep(.svg-icon) {
  color: #ffffff !important;
  fill: #ffffff !important;
}

:deep(.svg-icon svg) {
  color: #ffffff !important;
  fill: #ffffff !important;
}

:deep(.svg-icon svg path) {
  fill: #ffffff !important;
  stroke: #ffffff !important;
}

/* 选中状态的视觉样式：渐变背景 + 阴影 + 毛玻璃效果 */
.icon-select {
  background: linear-gradient(180deg, #c67be7 0%, #4d6afb 100%);
  box-shadow: 22.9696px 22.9696px 33.9814px rgba(0, 0, 0, 0.05);
  backdrop-filter: blur(25.486px);
}
// 二级菜单选中样式
.second-menu-item-selected {
  // border-radius: 8px;
  background: linear-gradient(90deg, #c67be7 0%, #4d6afb 100%);
  box-shadow: 0 2px 8px rgba(198, 123, 231, 0.3);
}

/* 箭头容器样式 - 确保箭头颜色为白色 */
.arrow-container {
  color: #f1f1f1ff;
}

.arrow-container :deep(svg) {
  color: #ffffff !important;
  fill: #ffffff !important;
}

.arrow-container :deep(path) {
  fill: #a19898ff !important;
  // stroke: #131212ff !important;
}

/* 文本区域淡入淡出动画效果 */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* 二级菜单滑动展开动画 */
.slide-down-enter-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-down-leave-active {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-down-enter-from {
  opacity: 0;
  transform: translateY(-10px);
  max-height: 0;
}

.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-5px);
  max-height: 0;
}

.slide-down-enter-to,
.slide-down-leave-from {
  opacity: 1;
  transform: translateY(0);
  max-height: 500px;
}

/* 二级菜单样式 */
.secondary-menu {
  overflow: hidden;
}

.secondary-menu-item {
  font-size: 14px;
  font-weight: 500;
}

/* 导航项包装器 */
.nav-item-wrapper {
  width: 100%;
}
</style>
